import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Send, Mic, Upload } from "lucide-react";
import { toast } from "sonner";

// Indian RTO format: 2 letters + 1-2 digits + 1-3 letters + 1-4 digits.
// Hyphens/spaces optional. Examples: MH01AB1234, MH-01-AB-1234, DL3CAB1234.
const RTO_RE = /^[A-Z]{2}[-\s]?\d{1,2}[-\s]?[A-Z]{1,3}[-\s]?\d{1,4}$/;
const normalizePlate = (s) => (s || "").toUpperCase().replace(/\s+/g, "").trim();

export default function Simulator() {
  const [mechanics, setMechanics] = useState([]);
  const [selected, setSelected] = useState("");
  const [convo, setConvo] = useState([]);
  const [text, setText] = useState("");
  const [carNumber, setCarNumber] = useState("");
  const [voiceTranscript, setVoiceTranscript] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/mechanics").then((r) => {
      setMechanics(r.data);
      if (r.data.length && !selected) setSelected(r.data[0].id);
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!selected) return;
    api.get(`/conversations/${selected}`).then((r) => setConvo(r.data));
    api.get(`/mechanics/${selected}/last-car-number`)
      .then((r) => setCarNumber(r.data.car_number || ""))
      .catch(() => setCarNumber(""));
  }, [selected]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [convo]);

  const refresh = () => api.get(`/conversations/${selected}`).then((r) => setConvo(r.data));

  const sendText = async (e) => {
    e?.preventDefault();
    if (!text.trim() || !selected) return;
    const cn = normalizePlate(carNumber);
    if (cn && !RTO_RE.test(cn)) {
      toast.error("Invalid Indian car number — expected like MH01AB1234 or DL3CAB1234");
      return;
    }
    setBusy(true);
    try {
      await api.post("/simulator/text", {
        mechanic_id: selected,
        text,
        car_number: cn || null,
      });
      setText("");
      // keep car number filled — same mechanic often works on the same vehicle
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  const sendVoiceTranscript = async () => {
    if (!voiceTranscript.trim() || !selected) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("mechanic_id", selected);
      fd.append("transcript", voiceTranscript);
      await api.post("/simulator/voice-text", fd);
      setVoiceTranscript("");
      toast.success("Voice note structured & logged");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const sendVoiceFile = async (file) => {
    if (!file || !selected) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("mechanic_id", selected);
      fd.append("file", file);
      await api.post("/simulator/voice", fd);
      toast.success("Voice transcribed via Whisper");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Transcription failed");
    } finally {
      setBusy(false);
    }
  };

  const sel = mechanics.find((m) => m.id === selected);

  return (
    <div className="p-8 space-y-6" data-testid="simulator-page">
      <header>
        <div className="overline">// whatsapp · simulator</div>
        <h1 className="font-display font-black text-3xl mt-1">WhatsApp Chat Simulator</h1>
        <p className="text-zinc-500 text-sm mt-1">Test the AI engine as if a mechanic was messaging on WhatsApp. Real DTC diagnostics, voice-note structuring, points awarded.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-3">
        {/* Mechanic selector */}
        <div className="gs-card p-4 space-y-2">
          <div className="overline mb-2">Acting As</div>
          {mechanics.map((m) => (
            <button
              key={m.id}
              data-testid={`select-mech-${m.id}`}
              onClick={() => setSelected(m.id)}
              className={`w-full text-left p-2 transition-colors ${
                selected === m.id ? "bg-amber-500/10 border border-amber-500/40" : "border border-transparent hover:bg-zinc-900/40"
              }`}
            >
              <div className="text-sm text-zinc-100">{m.name}</div>
              <div className="text-[11px] text-zinc-600 font-mono-data">{m.phone}</div>
              <div className="text-[11px] text-zinc-500 mt-0.5">{m.workshop_name} · {m.city}</div>
            </button>
          ))}
        </div>

        {/* Chat pane */}
        <div className="gs-card overflow-hidden flex flex-col h-[640px]">
          {/* WhatsApp header */}
          <div className="px-4 py-3 border-b border-zinc-900 bg-[#202C33] flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-sm font-display font-bold text-amber-500">
              {sel?.name?.[0] || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-zinc-100">{sel?.name || "—"}</div>
              <div className="text-[11px] text-zinc-500 font-mono-data">GarajSync AI · online</div>
            </div>
            <span className="text-[10px] font-mono-data text-zinc-600">END-TO-END · AI</span>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="wa-pane flex-1 overflow-y-auto p-4 space-y-2" data-testid="chat-messages">
            {convo.map((c) => (
              <div key={c.id} className={`flex ${c.direction === "in" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] px-3 py-2 text-sm ${c.kind === "system" ? "wa-system" : c.direction === "in" ? "wa-bubble-out" : "wa-bubble-in"}`}>
                  {c.kind === "voice" && <div className="text-[10px] opacity-70 mb-1 font-mono-data">voice · transcribed</div>}
                  <div className="whitespace-pre-wrap">{c.content}</div>
                  {c.meta?.confidence ? (
                    <div className="mt-2 pt-2 border-t border-white/10 space-y-1">
                      {c.meta.car_number && <span className="confidence-badge mr-1" style={{background:'rgba(245,158,11,0.12)',color:'#FCD34D',borderColor:'rgba(245,158,11,0.4)'}}>{c.meta.car_number}</span>}
                      {c.meta.dtc_code && <span className="dtc-badge mr-1">{c.meta.dtc_code}</span>}
                      <span className="confidence-badge">CONF {c.meta.confidence}%</span>
                      {c.meta.causes?.length > 0 && (
                        <div className="text-[11px] opacity-80 mt-1">
                          <div className="font-mono-data text-amber-300/80">Causes · संभावित कारण:</div>
                          <ul className="list-disc list-inside space-y-1">{c.meta.causes.map((x, i) => <li key={i} className="whitespace-pre-line">{x}</li>)}</ul>
                        </div>
                      )}
                      {c.meta.checks?.length > 0 && (
                        <div className="text-[11px] opacity-80">
                          <div className="font-mono-data text-amber-300/80">Recommended checks · सुझाव:</div>
                          <ol className="list-decimal list-inside space-y-1">{c.meta.checks.map((x, i) => <li key={i} className="whitespace-pre-line">{x}</li>)}</ol>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="wa-bubble-in px-3 py-2 text-xs font-mono-data">
                  AI is thinking <span className="ascii-load" />
                </div>
              </div>
            )}
          </div>

          {/* Input bar */}
          <form onSubmit={sendText} className="border-t border-zinc-900 p-3 bg-[#1C1F26] space-y-2">
            <div className="flex gap-2">
              <input
                data-testid="sim-car-number-input"
                value={carNumber}
                onChange={(e) => setCarNumber(e.target.value.toUpperCase())}
                placeholder="CAR #  e.g. MH01AB1234"
                className={`w-56 bg-[#090A0B] border px-3 py-2 text-xs outline-none font-mono-data uppercase tracking-wider text-amber-300 ${
                  !carNumber || RTO_RE.test(normalizePlate(carNumber))
                    ? "border-amber-500/40 focus:border-amber-500"
                    : "border-red-500/60 focus:border-red-500"
                }`}
              />
              <input
                data-testid="sim-text-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="e.g.  2019 Swift P0300"
                className="flex-1 bg-[#090A0B] border border-zinc-800 px-3 py-2 text-sm outline-none focus:border-amber-500 font-mono-data"
              />
              <input
                ref={fileRef}
                type="file"
                accept="audio/*"
                className="hidden"
                onChange={(e) => sendVoiceFile(e.target.files?.[0])}
              />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
                data-testid="sim-upload-audio-btn"
                className="px-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 disabled:opacity-50" title="Upload audio (Whisper STT)">
                <Upload className="w-4 h-4" />
              </button>
              <button type="submit" disabled={busy || !text.trim()}
                data-testid="sim-send-btn"
                className="px-4 bg-amber-500 hover:bg-amber-600 text-black font-display font-bold uppercase tracking-wider text-xs disabled:opacity-50 flex items-center gap-1.5">
                <Send className="w-3.5 h-3.5" /> Send
              </button>
            </div>

            {/* Voice transcript shortcut */}
            <div className="flex gap-2">
              <div className="flex-1 flex items-center gap-2 bg-[#090A0B] border border-zinc-800 px-3 py-1.5">
                <Mic className="w-3.5 h-3.5 text-amber-500/80" />
                <input
                  data-testid="sim-voice-transcript-input"
                  value={voiceTranscript}
                  onChange={(e) => setVoiceTranscript(e.target.value)}
                  placeholder='voice note text · e.g. "Swift ka front shocker replace kiya"'
                  className="flex-1 bg-transparent outline-none text-xs font-mono-data"
                />
              </div>
              <button type="button" disabled={busy || !voiceTranscript.trim()} onClick={sendVoiceTranscript}
                data-testid="sim-voice-send-btn"
                className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-display font-bold uppercase tracking-wider disabled:opacity-50">
                Log Voice
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
